import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import { ReauthFlow } from './ReauthFlow';
import { useHaptic } from '../../hooks/useHaptic';
import { useMe } from '../../hooks/useMe';
import { useTelegram } from '../../hooks/useTelegram';
import { hasApiAuth } from '../../lib/webSession';
import { useWallet } from '../../hooks/useWallet';
import { useTransfers } from '../../hooks/useTransfers';
import { useFeeQuote } from '../../hooks/useFeeQuote';
import { useStaking, useStakingActions } from '../../hooks/useStaking';
import { SourceSelect, buildPaySources, pickRichestPaySource } from './SourceSelect';
import { stakingErrorMessage, type Vault } from '../../lib/staking';
import { loadStakingLegal, openStakingLegal, saveStakingLegal } from '../../lib/stakingLegal';
import { fmtWhole, vaultKey } from './stakeMeta';

type Props = {
  open: boolean;
  amount: number;
  vault: Vault;
  /** USDT TRC-20 address deposits are paid to; null = staking unavailable. */
  treasury: string | null;
  onClose: () => void;
  onDone: (message: string) => void;
};

/**
 * Confirm → send USDT TRC-20 to the staking treasury → open the position.
 *
 * Deliberately the same two-step shape as a presale purchase: the money moves
 * first and the position is credited against that settled transfer, so a
 * failure after the send can be retried without ever sending twice.
 */
export function StakeConfirmSheet({ open, amount, vault, treasury, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const { impact, notify } = useHaptic();
  const { me } = useMe();
  const { initData } = useTelegram();
  const { wallet, balances } = useWallet();
  const { send } = useTransfers();
  const { legal } = useStaking();
  const { open: openPosition } = useStakingActions();

  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [legalOk, setLegalOk] = useState(loadStakingLegal);
  /** Survives re-renders so crediting can be retried without a second send. */
  const [paidTransferId, setPaidTransferId] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const payIdemRef = useRef<string | null>(null);
  const paidTransferIdRef = useRef<string | null>(null);
  const paidAmountRef = useRef<number | null>(null);

  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;
  const sources = buildPaySources(wallet, balances, t('nav.wallet')).filter(
    (s) => s.chain === 'trx' && s.token === 'USDT',
  );
  const source = sources.find((s) => s.key === sourceKey) ?? pickRichestPaySource(sources);

  const {
    feeUsd: networkFee,
    maxFeeUsd,
    fresh: feeFresh,
  } = useFeeQuote('trx', 'USDT', treasury ?? undefined, source?.address);
  const reserveFee = maxFeeUsd ?? networkFee ?? 0;
  const totalReserved = amount + (feeFresh ? reserveFee : 0);
  const canPay =
    !!source &&
    !!treasury &&
    !!wallet?.id &&
    feeFresh &&
    (paidTransferId ? true : source.balance >= totalReserved);

  const markPaid = (id: string, paid: number) => {
    paidTransferIdRef.current = id;
    paidAmountRef.current = paid;
    setPaidTransferId(id);
  };

  const clearPaid = () => {
    paidTransferIdRef.current = null;
    paidAmountRef.current = null;
    payIdemRef.current = null;
    setPaidTransferId(null);
  };

  // Editing the amount after a partial success must drop the stale payment, or
  // a smaller on-chain send would be credited against a larger quote.
  useEffect(() => {
    if (paidAmountRef.current != null && paidAmountRef.current !== amount) clearPaid();
  }, [amount]);

  // A different wallet is a different on-chain send. Ignore the first resolved
  // source so a balance refresh doesn't wipe a key already in flight.
  const prevSourceKey = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const key = source?.key ?? null;
    if (prevSourceKey.current === undefined) {
      prevSourceKey.current = key;
      return;
    }
    if (prevSourceKey.current === key) return;
    prevSourceKey.current = key;
    if (paidTransferIdRef.current || submittingRef.current) return;
    payIdemRef.current = null;
  }, [source?.key]);

  const credit = async (transferId: string) => {
    await openPosition.mutateAsync({ vault, amount, paymentTransferId: transferId });
    notify('success');
    onDone(t('stake.staked', { amount: fmtWhole(amount), vault: t(vaultKey(vault.kind, 'Name')) }));
    clearPaid();
    onClose();
  };

  /** Credit only — the USDT is already on-chain. Never send again. */
  const retryCredit = async () => {
    const transferId = paidTransferIdRef.current;
    if (!transferId || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      impact('medium');
      await credit(transferId);
    } catch (e) {
      notify('error');
      const msg = String(e);
      if (msg.includes('reauth_required')) setReauthOpen(true);
      else setError(stakingErrorMessage(msg, t, t('stake.errGeneric')));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const runStake = async (token?: string) => {
    if (!wallet?.id || !source || !treasury) return;
    if (paidTransferIdRef.current) return retryCredit();
    if (vault.closed) {
      notify('error');
      setError(t('stake.errClosed'));
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      impact('medium');
      if (!payIdemRef.current) payIdemRef.current = crypto.randomUUID();
      const transfer = await send.mutateAsync({
        walletId: wallet.id,
        chain: 'trx',
        token: 'USDT',
        to: treasury,
        amount: String(amount),
        reauthToken: token,
        fromAddressId: source.addressId,
        fromIndex: source.fromIndex,
        idempotencyKey: payIdemRef.current,
        // The position is opened against a settled transfer, so wait for it.
        awaitSettlement: true,
      });
      markPaid(transfer.id, amount);
      await credit(transfer.id);
    } catch (e) {
      notify('error');
      const msg = String(e);
      if (msg.includes('reauth_required')) setReauthOpen(true);
      // Keep the sheet open when USDT already left — the user must be able to
      // retry crediting without paying again.
      else setError(stakingErrorMessage(msg, t, t('stake.errGeneric')));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const submit = () => {
    if ((!canPay && !paidTransferId) || busy) return;
    if (!paidTransferId && !legalOk) return;
    if (paidTransferId) return void retryCredit();
    if (needsReauth) return setReauthOpen(true);
    void runStake();
  };

  return (
    <>
      <BottomSheet open={open} onClose={busy || paidTransferId ? () => {} : onClose}>
        <div className="pb-2">
          <div className="mb-3.5 text-[20px] font-bold tracking-[-0.5px] text-ink">
            {t('stake.confirmTitle')}
          </div>

          <Row label={t('stake.amount')} value={fmtWhole(amount)} />
          <Row label={t('stake.planType')} value={t(vaultKey(vault.kind, 'Name'))} />
          <Row
            label={t('stake.apyLabel')}
            value={t('stake.apyShort', { min: vault.apyMin, max: vault.apyMax })}
            valueColor="#22b45e"
          />
          <Row
            label={t('stake.sheetNetwork')}
            value={feeFresh ? `$${reserveFee.toFixed(2)}` : '…'}
            last
          />

          {!paidTransferId && sources.length > 0 && (
            <div className="mt-3">
              <span className="text-[13px] text-muted">{t('cards.payFrom')}</span>
              <div className="mt-2">
                <SourceSelect
                  sources={sources}
                  value={source?.key ?? null}
                  onChange={(key) => {
                    if (busy || paidTransferId) return;
                    setSourceKey(key);
                  }}
                  required={totalReserved}
                />
              </div>
            </div>
          )}

          <p className="mt-2.5 text-[12px] leading-[1.5] text-muted">{t('stake.noteConfirm')}</p>

          {!paidTransferId && (
            <label className="mt-3 flex items-start gap-3 rounded-[14px] bg-black/[0.03] px-3 py-2.5 active:bg-black/[0.05]">
              <input
                type="checkbox"
                checked={legalOk}
                disabled={busy}
                onChange={(e) => {
                  const v = e.target.checked;
                  setLegalOk(v);
                  saveStakingLegal(v);
                }}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[#377dff]"
              />
              <span className="text-[13px] leading-relaxed text-muted">
                <Trans
                  i18nKey="stake.agreeLegal"
                  components={{
                    terms: <LegalDocLink url={legal.terms} />,
                    aml: <LegalDocLink url={legal.aml} />,
                    privacy: <LegalDocLink url={legal.privacy} />,
                  }}
                />
              </span>
            </label>
          )}

          {!treasury && (
            <p className="mt-2 text-[12px] font-semibold text-negative">
              {t('stake.errUnavailable')}
            </p>
          )}
          {treasury && !paidTransferId && source && source.balance < totalReserved && (
            <p className="mt-2 text-[12px] font-semibold text-negative">
              {t('send.errInsufficient')}
            </p>
          )}
          {error && <p className="mt-2 text-[12px] font-semibold text-negative">{error}</p>}

          <div className="mt-4 flex gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={busy || !!paidTransferId}
              className="h-11 flex-1 rounded-[14px] border-[0.5px] border-black/10 bg-black/[0.05] text-[15px] font-semibold text-muted active:bg-black/[0.08] disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={(!canPay && !paidTransferId) || busy || (!paidTransferId && !legalOk)}
              className="h-11 flex-[2] rounded-[14px] text-[15px] font-semibold text-white active:opacity-90 disabled:opacity-50"
              style={{ background: '#34c759' }}
            >
              {busy
                ? t('common.loading')
                : paidTransferId
                  ? t('common.retry')
                  : t('stake.stakeNow')}
            </button>
          </div>
        </div>
      </BottomSheet>

      {reauthOpen && hasApiAuth(initData) && (
        <div className="push-enter fixed inset-0 z-[70] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              setReauthOpen(false);
              void runStake(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}
    </>
  );
}

function LegalDocLink({ url, children }: { url: string; children?: ReactNode }) {
  return (
    <button
      type="button"
      className="font-medium text-[#377dff] underline"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openStakingLegal(url);
      }}
    >
      {children}
    </button>
  );
}

function Row({
  label,
  value,
  valueColor,
  last,
}: {
  label: string;
  value: string;
  valueColor?: string;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between py-2.5 ${last ? '' : 'border-b border-black/[0.06]'}`}
    >
      <span className="text-[13px] text-muted">{label}</span>
      <span className="text-[14px] font-medium" style={{ color: valueColor ?? '#121212' }}>
        {value}
      </span>
    </div>
  );
}
